import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff, KeyRound, Loader2 } from 'lucide-react';
import AuthLayout from '@/components/auth/AuthLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { resetPassword } from '@/lib/api';

const MIN_LENGTH = 8;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (password.length < MIN_LENGTH) {
      toast.error(`Password must be at least ${MIN_LENGTH} characters`);
      return;
    }
    if (password !== confirm) {
      toast.error('The two passwords do not match');
      return;
    }
    try {
      setSubmitting(true);
      await resetPassword({ token, new_password: password });
      toast.success('Password updated. Sign in with your new password.');
      navigate('/login', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Password could not be reset.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <AuthLayout subtitle="Password reset" title="This link is incomplete">
        <p className="text-sm text-[#062B1A]/70">
          The reset link is missing its token. Open the link from your email again, or request a new one
          from the sign-in page.
        </p>
        <Button asChild className="mt-6 h-11 w-full bg-[#062B1A] text-white">
          <Link to="/login">Back to sign in</Link>
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout subtitle="Password reset" title="Choose a new password">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="new-password" className="text-sm font-medium text-[#062B1A]">
            New password
          </Label>
          <div className="relative">
            <Input
              id="new-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={`At least ${MIN_LENGTH} characters`}
              data-testid="reset-password-input"
              className="h-11 pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword((value) => !value)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#062B1A]/50 hover:text-[#062B1A]"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirm-password" className="text-sm font-medium text-[#062B1A]">
            Confirm new password
          </Label>
          <Input
            id="confirm-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Type it again"
            data-testid="reset-password-confirm"
            className="h-11"
          />
        </div>

        <Button
          type="submit"
          disabled={submitting}
          data-testid="reset-password-submit"
          className="h-11 w-full bg-[#062B1A] text-white"
        >
          {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
          Update password
        </Button>

        <p className="text-center text-xs text-[#062B1A]/55">
          Reset links work once and expire an hour after they are sent.{' '}
          <Link to="/login" className="font-medium text-[#062B1A] hover:text-[#D4AF37]">
            Back to sign in
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
